import { Box, Tooltip, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import Messages from "../../../../../localization/Messages";
import ChartComponent from "../../ChartComponent/ChartComponent";
import { ExpenseStatsDock } from "../../../../../models/stats";
import { formatNumber } from "../../../../../helpers/numberDisplayHelper";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type OnCallAnalyticsProps = {
    expenseStatsDock: ExpenseStatsDock[];
};

export const OnCallAnalytics = ({ expenseStatsDock }: OnCallAnalyticsProps) => {
    const intl = useIntl();
    const dataAvailable = expenseStatsDock.length > 0;
    const totals = dataAvailable
        ? expenseStatsDock.reduce(
              (acc, dock) => {
                  acc.serviceSatCount += dock.serviceSatCount;
                  acc.serviceSunCount += dock.serviceSunCount;
                  acc.serviceRegularCount += dock.serviceRegularCount;
                  acc.serviceTotalCount +=
                      dock.serviceSatCount + dock.serviceSunCount + dock.serviceRegularCount;
                  acc.waterMeterTotal += dock.serviceCount0;
                  acc.powerLockTotal += dock.serviceCount1;
                  acc.faecesAdapterTotal += dock.serviceCount2;
                  acc.extensionCableTotal += dock.serviceCount3;
                  acc.improperDisposalTotal += dock.serviceCount4;
                  acc.otherExpenditureTotal += dock.serviceCount5;
                  acc.serviceByTypeTotalCount +=
                      dock.serviceCount0 +
                      dock.serviceCount1 +
                      dock.serviceCount2 +
                      dock.serviceCount3 +
                      dock.serviceCount4 +
                      dock.serviceCount5;
                  return acc;
              },
              {
                  serviceSatCount: 0,
                  serviceSunCount: 0,
                  serviceRegularCount: 0,
                  serviceTotalCount: 0,
                  waterMeterTotal: 0,
                  powerLockTotal: 0,
                  faecesAdapterTotal: 0,
                  extensionCableTotal: 0,
                  improperDisposalTotal: 0,
                  otherExpenditureTotal: 0,
                  serviceByTypeTotalCount: 0,
              },
          )
        : {
              serviceSatCount: 0,
              serviceSunCount: 0,
              serviceRegularCount: 0,
              serviceTotalCount: 0,
              waterMeterTotal: 0,
              powerLockTotal: 0,
              faecesAdapterTotal: 0,
              extensionCableTotal: 0,
              improperDisposalTotal: 0,
              otherExpenditureTotal: 0,
              serviceByTypeTotalCount: 0,
          };

    const {
        serviceSatCount,
        serviceSunCount,
        serviceRegularCount,
        serviceTotalCount,
        waterMeterTotal,
        powerLockTotal,
        faecesAdapterTotal,
        extensionCableTotal,
        improperDisposalTotal,
        otherExpenditureTotal,
        serviceByTypeTotalCount,
    } = totals;

    return (
        <Box className="on-call-analytics-container shadow" my={2} mx={2.5} padding={2.5}>
            <Typography variant="h5">
                {intl.formatMessage(Messages.onCall)}
                <Tooltip title={intl.formatMessage(Messages.onlyInvoicedConfirmedChangedTooltip)}>
                    <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
            </Typography>
            <Box display={"flex"} justifyContent={"center"}>
                <Box width={"50%"}>
                    <ChartComponent
                        data={
                            dataAvailable
                                ? [
                                      {
                                          label: intl.formatMessage(Messages.weekdays),
                                          value: serviceRegularCount,
                                      },

                                      {
                                          label: intl.formatMessage(Messages.nightSaturday),
                                          value: serviceSatCount,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.sundayHolidays),
                                          value: serviceSunCount,
                                      },
                                  ]
                                : []
                        }
                        description={intl.formatMessage(Messages.numberHoursOfServices)}
                        outerStats={[
                            `${intl.formatMessage(
                                Messages.totalNumberHoursOfServices,
                            )}: ${serviceTotalCount.toFixed(2)}h`,
                        ]}
                        formatter={value =>
                            `${formatNumber(value)} / ${((value / serviceTotalCount) * 100).toFixed(
                                2,
                            )}%`
                        }
                    />
                </Box>

                <Box width={"50%"}>
                    <ChartComponent
                        data={
                            dataAvailable
                                ? [
                                      {
                                          label: intl.formatMessage(Messages.waterMeter),
                                          value: waterMeterTotal,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.powerlockAdapter),
                                          value: powerLockTotal,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.faecesAdapter),
                                          value: faecesAdapterTotal,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.extensionCable),
                                          value: extensionCableTotal,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.improperDisposal),
                                          value: improperDisposalTotal,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.otherExpenditure),
                                          value: otherExpenditureTotal,
                                      },
                                  ]
                                : []
                        }
                        description={intl.formatMessage(Messages.numberHoursOfServicesPerType)}
                        outerStats={[]}
                        formatter={value =>
                            `${value} / ${((value / serviceByTypeTotalCount) * 100).toFixed(2)}%`
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};
